import { useCallback, useEffect, useMemo, useState } from 'react'
import Select from 'react-select'
import { toast } from 'react-toastify'
import clsx from 'clsx'
import Swal from 'sweetalert2'

import { TApi } from '../../../../shared/const'
import http from '../../../../shared/http'
import { mapCompanyTypeToName } from '../../../../shared/helpers'

import Btn from '../../../../UI/Btn'
import Loading from '../../../../UI/Loading'
import MiniHeader from '../../../../UI/MiniHeader'

import ScoreField from '../../common/Concept/ScoreField'
import HandbookButton from '../../common/Concept/HandbookButton'

import CourseStanding from '../../common/CourseStanding'
import ConceptProgress from '../../../../UI/ConceptProgress'

import styles from './style.module.scss'

/**
 * Das Skills4Retail-Konzept aus Sicht der Lehrkraft: bewerten.
 *
 * Bewerten darf nur die **betreuende** Lehrkraft, und die Liste oben zeigt
 * deshalb nicht die zugeordneten Junior Companies, sondern die betreuten —
 * `GET teacher/concept` beantwortet genau diese Frage. Stünden dort auch die
 * zugeordneten, antwortete jeder Klick darauf mit 404.
 *
 * Jedes der fünfzehn Felder bekommt 0 bis 10 Punkte, jedes einzeln gespeichert.
 * Ein Formular über alle fünfzehn hätte bedeutet, dass eine unterbrochene
 * Bewertung nichts hinterlässt.
 *
 * Zwei Dinge daneben: das Konzept zur Überarbeitung zurückgeben und einzelne
 * Schüler:innen begründet von der Quizpflicht befreien. Beides steht danach im
 * Verlauf des Unternehmens.
 */

// Welche Junior Company zuletzt offen war — dieselbe Überlegung wie am
// Dashboard: Wer eine bestimmte betreut, will sie nicht jedes Mal neu wählen.
const SPEICHER_SCHLUESSEL = 'teacherConceptCompanyId'

const STATUS_TEXT = {
	DRAFT: 'Die Schüler:innen arbeiten noch daran.',
	RETURNED: 'Von Ihnen zur Überarbeitung zurückgegeben.',
	SUBMITTED: 'Zur Bewertung abgeschlossen — Sie sind dran.',
	SCORED: 'Bewertet.'
}

export default function Concept() {
	const [companies, setCompanies] = useState(null)
	const [companyId, setCompanyId] = useState(null)
	const [daten, setDaten] = useState(null)
	const [laedt, setLaedt] = useState(false)

	useEffect(() => {
		http.get(TApi.TEACHER_CONCEPT_LIST)
			.then(({ data }) => {
				setCompanies(data)
				if (!data.length) return

				const gemerkt = Number(window.localStorage.getItem(SPEICHER_SCHLUESSEL))
				const treffer = data.find(eintrag => eintrag.id === gemerkt)

				setCompanyId(treffer ? treffer.id : data[0].id)
			})
			.catch(() => setCompanies([]))
	}, [])

	useEffect(() => {
		if (!companyId) return

		window.localStorage.setItem(SPEICHER_SCHLUESSEL, String(companyId))
		setLaedt(true)
		setDaten(null)
		http.get(TApi.TEACHER_CONCEPT(companyId))
			.then(({ data }) => setDaten(data))
			.catch(() => {})
			.finally(() => setLaedt(false))
	}, [companyId])

	const bewerte = useCallback(
		(fieldNo, score) =>
			http
				.put(TApi.TEACHER_CONCEPT_SCORE(companyId, fieldNo), { score }, { disableToast: true })
				.then(({ data }) => setDaten(data)),
		[companyId]
	)

	const zurueckgeben = async () => {
		const { isConfirmed, value } = await Swal.fire({
			title: 'Konzept zur Überarbeitung zurückgeben?',
			input: 'textarea',
			inputLabel: 'Was soll überarbeitet werden? (steht danach an der Seite der Schüler:innen)',
			inputPlaceholder: 'z. B. Feld 8: Die fünf Keywords fehlen.',
			icon: 'question',
			showCancelButton: true,
			confirmButtonColor: '#008B9C',
			cancelButtonColor: '#6c757d',
			confirmButtonText: 'Zurückgeben',
			cancelButtonText: 'Abbrechen'
		})

		if (!isConfirmed) return

		http.post(TApi.TEACHER_CONCEPT_RETURN(companyId), { reason: value || '' })
			.then(({ data }) => {
				setDaten(data)
				toast.success('Das Konzept ist wieder bei den Schüler:innen.')
			})
			.catch(() => {})
	}

	/*
	 * Die Bewertung abschließen, ohne eine Punktzahl zu ändern.
	 *
	 * Der Knopf erscheint nur in einem Fall: Das Konzept war schon bewertet,
	 * wurde zurückgegeben und ist erneut abgeschlossen worden. Die Punkte von
	 * zuvor stehen dann alle noch da — ohne diesen Schritt käme die Junior
	 * Company nicht weiter, obwohl kein Feld leer ist.
	 */
	const abschliessen = async () => {
		const felder = daten?.fields || []
		const punkte = felder.reduce((wert, field) => wert + (field.score || 0), 0)

		const { isConfirmed } = await Swal.fire({
			title: 'Bewertung abschließen?',
			text: `Ihre Punkte von zuvor bleiben unverändert stehen — zusammen ${punkte} von ${
				felder.length * 10
			} Punkten. Die Junior Company kommt damit weiter.`,
			icon: 'question',
			showCancelButton: true,
			confirmButtonColor: '#008B9C',
			cancelButtonColor: '#6c757d',
			confirmButtonText: 'Abschließen',
			cancelButtonText: 'Abbrechen'
		})

		if (!isConfirmed) return

		http.post(TApi.TEACHER_CONCEPT_FINISH(companyId))
			.then(({ data }) => {
				setDaten(data)
				toast.success('Das Konzept ist bewertet.')
			})
			.catch(() => {})
	}

	const befreiung = async student => {
		if (student.exempt) {
			const { isConfirmed } = await Swal.fire({
				title: `Befreiung von ${student.name} aufheben?`,
				text: 'Danach müssen wieder alle zehn Kapitel bestanden werden.',
				icon: 'question',
				showCancelButton: true,
				confirmButtonColor: '#008B9C',
				cancelButtonColor: '#6c757d',
				confirmButtonText: 'Aufheben',
				cancelButtonText: 'Abbrechen'
			})

			if (!isConfirmed) return

			return http
				.post(TApi.TEACHER_CONCEPT_EXEMPTION(companyId), { userId: student.id, reason: '' })
				.then(({ data }) => setDaten(data))
				.catch(() => {})
		}

		const { isConfirmed, value } = await Swal.fire({
			title: `${student.name} von der Quizpflicht befreien?`,
			input: 'textarea',
			inputLabel: 'Grund (steht im Verlauf des Unternehmens)',
			inputPlaceholder: 'z. B. Nachteilsausgleich, längere Erkrankung',
			icon: 'question',
			showCancelButton: true,
			confirmButtonColor: '#008B9C',
			cancelButtonColor: '#6c757d',
			confirmButtonText: 'Befreien',
			cancelButtonText: 'Abbrechen',
			inputValidator: eingabe => (eingabe?.trim() ? undefined : 'Bitte einen Grund angeben.')
		})

		if (!isConfirmed) return

		http.post(TApi.TEACHER_CONCEPT_EXEMPTION(companyId), { userId: student.id, reason: value })
			.then(({ data }) => setDaten(data))
			.catch(() => {})
	}

	const auswahl = useMemo(
		() =>
			(companies || []).map(eintrag => ({
				value: eintrag.id,
				label: `${eintrag.name} — ${eintrag.fieldsScored} von ${eintrag.fieldsTotal} bewertet`
			})),
		[companies]
	)

	if (!companies) return <Loading />

	const company = (companies || []).find(eintrag => eintrag.id === companyId)
	const offen = daten ? (daten.fields || []).filter(field => field.score === null).length : 0
	const summe = daten ? (daten.fields || []).reduce((wert, field) => wert + (field.score || 0), 0) : 0
	const bewertbar = daten?.status === 'SUBMITTED' || daten?.status === 'SCORED'
	/*
	 * Alle Punkte stehen, das Konzept gilt aber noch nicht als bewertet: Das ist
	 * genau der Fall nach einer Rückgabe. Ohne den Knopf müsste die Lehrkraft
	 * ein Konzeptfeld erneut speichern, um weiterzukommen — und käme von
	 * selbst kaum darauf.
	 */
	const bestaetigen = daten?.status === 'SUBMITTED' && offen === 0

	return (
		<div className={styles.root}>
			<MiniHeader title="Konzepte bewerten">
				<HandbookButton />
			</MiniHeader>

			{!companies.length ? (
				<p className={styles.empty}>
					Sie betreuen zurzeit keine Junior Company. Bewerten kann nur die betreuende Lehrkraft — wer einer
					Junior Company nur zugeordnet ist, sieht deren Konzept nicht.
				</p>
			) : (
				<>
					<div className={styles.bar}>
						<div className={styles.barName}>
							<span className={styles.barLabel}>Junior Company</span>
							<h2 className={styles.barTitle}>{company?.name}</h2>
							<span className={styles.barMeta}>{mapCompanyTypeToName(company?.companyType)}</span>
						</div>

						{companies.length > 1 && (
							<Select
								className={styles.select}
								classNamePrefix="select"
								options={auswahl}
								value={auswahl.find(option => option.value === companyId) || null}
								onChange={option => setCompanyId(option.value)}
								isSearchable={auswahl.length > 7}
								aria-label="Junior Company"
							/>
						)}
					</div>

					{laedt && <Loading />}

					{daten && (
						<>
							<div className={clsx(styles.status, daten.status === 'SUBMITTED' && styles.statusDue)}>
								<div>
									<b>{STATUS_TEXT[daten.status] || daten.status}</b>
									{bewertbar && (
										<p className={styles.statusHint}>
											{offen > 0
												? `Noch ${offen} von ${daten.fields.length} Feldern ohne Punkte — insgesamt bisher ${summe} Punkte.`
												: `Alle ${daten.fields.length} Felder bewertet, zusammen ${summe} von ${
														daten.fields.length * 10
												  } Punkten.`}
										</p>
									)}
									{bestaetigen && (
										<p className={styles.statusHint}>
											Ihre Punkte von der letzten Bewertung stehen noch. Sehen Sie die
											Überarbeitung durch und ändern Sie, was zu ändern ist — oder schließen Sie
											die Bewertung unverändert ab. Erst damit kommt die Junior Company weiter.
										</p>
									)}
									{daten.status === 'RETURNED' && daten.returnReason && (
										<p className={styles.statusHint}>Ihr Hinweis: „{daten.returnReason}“</p>
									)}

									{/*
									 * Derselbe Balken wie bei den Schüler:innen und in der
									 * Verwaltung, aus derselben Rechnung. Er sagt in einem
									 * Blick, ob eine Junior Company hinterherhängt — ohne
									 * dass man die Abschnitte einzeln durchsehen muss.
									 */}
									<ConceptProgress progress={daten.progress} className={styles.statusProgress} />
								</div>

								<div className={styles.statusActions}>
									{bestaetigen && (
										<Btn theme="primary" onClick={abschliessen}>
											Bewertung abschließen
										</Btn>
									)}
									{bewertbar && (
										<Btn theme="secondary" onClick={zurueckgeben}>
											Zur Überarbeitung zurückgeben
										</Btn>
									)}
								</div>
							</div>

							<section className={styles.students}>
								<h3 className={styles.blockTitle}>Lernstrecke der Schüler:innen</h3>
								<p className={styles.blockHint}>
									Zehn Kapitel, jedes mit fünf Fragen. Bestanden ist ein Kapitel ab{' '}
									{daten.config.quizPass} richtigen Antworten. Wer aus gutem Grund nicht daran
									teilnehmen kann, wird hier befreit — die Befreiung steht im Verlauf des
									Unternehmens.
								</p>
								{/*
								 * Wie viele die Strecke abschließen müssen, steht in den Einstellungen und ist
								 * je Unternehmensform verschieden — dieselbe Auskunft wie bei Schüler:innen
								 * und Verwaltung, damit alle drei dieselbe Antwort auf „woran hängt es?"
								 * bekommen. `ohneListe`, weil die Namen gleich darunter stehen.
								 */}
								<CourseStanding
									state={daten.state}
									students={daten.students}
									config={daten.config}
									ohneListe
								/>
								<ul className={styles.studentList}>
									{daten.students.map(student => (
										<li key={student.id} className={styles.student}>
											<span className={styles.studentName}>{student.name}</span>
											<span
												className={clsx(
													styles.studentProgress,
													student.chaptersPassed >= daten.config.chapters &&
														styles.studentDone
												)}
											>
												{student.exempt
													? 'befreit'
													: `${student.chaptersPassed} von ${daten.config.chapters} Kapiteln`}
											</span>
											{student.exempt && student.exemptReason && (
												<span className={styles.studentReason}>„{student.exemptReason}“</span>
											)}
											<button
												type="button"
												className={styles.linkBtn}
												onClick={() => befreiung(student)}
											>
												{student.exempt ? 'Befreiung aufheben' : 'von der Quizpflicht befreien'}
											</button>
										</li>
									))}
									{!daten.students.length && (
										<li className={styles.student}>
											Diese Junior Company hat noch keine Schüler:innen.
										</li>
									)}
								</ul>
							</section>

							<section className={styles.fields}>
								<h3 className={styles.blockTitle}>Die fünfzehn Konzeptfelder</h3>
								<p className={styles.blockHint}>
									{bewertbar
										? 'Je Feld 0 bis 10 Punkte. Jede Eingabe wird sofort gespeichert; Sie können jederzeit aufhören und später weitermachen.'
										: 'Bewerten können Sie, sobald die Schüler:innen ihr Konzept abgeschlossen haben.'}
								</p>

								{(daten.fields || []).map(field => (
									<ScoreField
										key={field.no}
										field={field}
										students={daten.students}
										readOnly={!bewertbar}
										onScore={bewerte}
									/>
								))}
							</section>
						</>
					)}
				</>
			)}
		</div>
	)
}
